import React, {
  useMemo,
  useState,
  useCallback,
  useEffect,
} from 'react';
import { AgGridReact } from '@ag-grid-community/react';
import { Button } from 'components/common/Button';
import {
  DeleteIcon,
  FilterIcon,
  HelpIcon,
  MenuIcon,
  PlusIcon,
  RefreshIcon,
  ReorderLineIcon,
} from 'assets/icons';
import { AgGridContainer } from 'components/common/AgGridContainer';
import {
  ButtonsContainer,
  Divider,
} from 'components/common/ButtonsContainer';
import { FilteredBy } from 'components/common/FilteredBy';
import { Filters } from 'components/common/Filters';
import { LoadingBar } from 'components/common/LoadingBar';
import { StyledQuantityDisplay } from 'components/Tables/styled';
import { useFilters } from 'hooks/useFilters';
import { usePopper } from 'react-popper';
import {
  ColDef,
  GridOptions,
  GridReadyEvent,
  IServerSideDatasource,
  IServerSideGetRowsParams,
  IServerSideSelectionState,
} from '@ag-grid-community/core';
import {
  commonGridOptions,
  deselectAll,
} from 'utils/aggrid';
import { AgGridEmptyState } from 'components/common/AgGridEmptyState';
import { Filter } from 'interfaces/filters';
import { Subscriptions } from 'interfaces/subscriptions';
import { ContractLines } from 'interfaces/contractLines';
import { useDragColumnChange } from 'hooks/useDragColumnChange';
import { SearchResponse } from 'interfaces/api';
import { mergeAll } from 'ramda';
import { PER_PAGE } from 'utils/constants';
import { useDebouncedEffect } from 'hooks/useDebouncedEffect';
import {
  useAppDispatch,
  useAppSelector,
} from 'hooks/redux';
import { gridHelperActions } from 'store/slices/gridHelper';
import { gridHelperSelector } from 'store/selectors/gridHelper';
import { FactaTableHelp } from 'components/FactaTableHelp';
import { Tooltip } from 'components/common/Tooltip';
import { AgGridLoadingCellRenderer } from 'components/common/AgGridLoadingCellRenderer';

interface Props {
  gridRef: React.RefObject<AgGridReact>;
  filtersDefs: Filter[];
  columnDefs: ColDef[];
  isDisabled?: boolean;
  onClickAdd?: () => void;
  onClickDelete?: () => void;
  entityName: string;
  gridOptions?: GridOptions;
  customEmptyState?: () => JSX.Element;
  subscriptionsOptions?: Subscriptions;
  contractLinesOptions?: ContractLines;
  useLazyQuery: () => any[];
  quickSearch: string;
}

export const FactaTablePaginated = ({
  gridRef,
  filtersDefs,
  columnDefs,
  isDisabled,
  onClickAdd,
  onClickDelete,
  entityName,
  gridOptions,
  customEmptyState,
  subscriptionsOptions,
  contractLinesOptions,
  useLazyQuery,
  quickSearch,
}: Props) => {
  const dispatch = useAppDispatch();
  const gridHelper = useAppSelector(gridHelperSelector);
  const [compactMode, setCompactMode] = useState(localStorage.getItem('compactMode') === 'true');
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [isColumnsChanged, setIsColumnsChanged] = useState(false);
  const [isHelpVisible, setIsHelpVisible] = useState(false);
  const [referenceElement, setReferenceElement] = useState<HTMLButtonElement | null>(null);
  const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(null);
  const [search, setSearch] = useState(quickSearch);
  const [isLoadFailed, setIsLoadFailed] = useState(false);
  const { styles, attributes, update } = usePopper(referenceElement, popperElement, {
    placement: 'bottom-start',
    strategy: 'fixed',
    modifiers: [{
      name: 'preventOverflow',
      options: {
        mainAxis: false,
        altAxis: true,
        padding: 40,
      },
    }],
  });

  const {
    onDragStarted,
    onDragStopped,
  } = useDragColumnChange(() => setIsColumnsChanged(true));

  const [getData, { isLoading, isFetching }] = useLazyQuery();

  const initialFilters = useMemo(() => filtersDefs, []); // eslint-disable-line react-hooks/exhaustive-deps

  const {
    filters,
    setFilters,
    setFilter,
    clearAllFilters,
    appliedFilters,
  } = useFilters(initialFilters);

  const createDatasource = useMemo(() => (appliedFilters?: Filter[]): IServerSideDatasource => ({
    getRows: (params: IServerSideGetRowsParams) => {
      const page = (params.request.startRow! / PER_PAGE);

      const filterOptions = mergeAll(appliedFilters
        ?.map((filter) => ({ [filter.name]: filter.value })) || []);

      getData({
        pagination: {
          page,
          perPage: PER_PAGE,
        },
        filtering: {
          search: search,
          deleted: false,
          ...filterOptions,
        },
        sortModel: params.request.sortModel,
      })
        .unwrap()
        .then((result: SearchResponse<any>) => {
          params.success({ rowData: result.data, rowCount: result.total });
          dispatch(gridHelperActions.setTotal(result.total));
        })
        .catch(() => {
          params.fail();
          setIsLoadFailed(true);
        });
    },
  }), [dispatch, getData, search]);

  const onGridReady = (e: GridReadyEvent) => {
    e.api.setGridOption('serverSideDatasource', createDatasource());
  };

  const combinedGridOptions: GridOptions = {
    ...commonGridOptions,
    ...gridOptions,
    onDragStarted: onDragStarted,
    onDragStopped: onDragStopped,
    onColumnPinned: () => setIsColumnsChanged(true),
    onColumnVisible: () => setIsColumnsChanged(true),
    onGridReady: onGridReady,
    cacheBlockSize: PER_PAGE,
    maxBlocksInCache: 5,
    rowModelType: "serverSide",
    blockLoadDebounceMillis: 500,
    onSelectionChanged: (e) => {
      const selectionState = e.api.getServerSideSelectionState() as IServerSideSelectionState;
      dispatch(gridHelperActions.setSelectionState(selectionState));
    },
  };

  const handleToggleFilter = () => {
    setIsFiltersOpen(!isFiltersOpen);

    if (update) {
      update();
    }
  };

  const handleClickResetView = useCallback(() => {
    gridRef.current!.api.resetColumnState();
    setIsColumnsChanged(false);
  }, [gridRef]);

  useEffect(() => {
    setTimeout(() => {
      gridRef.current?.api?.setGridOption('serverSideDatasource', createDatasource(appliedFilters));
    }, 0);
  }, [appliedFilters, createDatasource, gridRef]);

  useEffect(() => {
    if (isLoading) {
      setTimeout(() => gridRef.current?.api?.showLoadingOverlay(), 0);
    } else if (gridHelper.total === 0) {
      setTimeout(() => gridRef.current?.api?.showNoRowsOverlay(), 0);
    } else {
      setTimeout(() => gridRef.current?.api?.hideOverlay(), 0);
    }
  }, [gridHelper.total, gridRef, isLoading]);

  useDebouncedEffect(() => {
    setSearch(quickSearch);
    dispatch(gridHelperActions.setQuickSearchFiltering({ search: quickSearch }));
  }, [quickSearch], 300);

  useEffect(() => {
    if (gridRef.current?.api) {
      deselectAll(gridRef);
    }
  }, [appliedFilters, gridRef]);

  useEffect(() => () => {
    dispatch(gridHelperActions.clear());
  }, [dispatch]);

  useEffect(() => {
    localStorage.setItem('compactMode', compactMode ? 'true' : 'false');
  }, [compactMode]);

  const NoRowsOverlay = () => (
    <AgGridEmptyState>
      You don’t have any {entityName.toLowerCase()}s set up yet. Add a {entityName.toLowerCase()} to get started.
      <Button
        variant="contained"
        size="normal"
        onClick={onClickAdd}
      >
        ADD {entityName.toUpperCase()}
      </Button>
    </AgGridEmptyState>
  );

  const LoadingOverlay = () => (
    <AgGridEmptyState>
      Loading...
    </AgGridEmptyState>
  );

  return (
    <>
      <ButtonsContainer>
        <StyledQuantityDisplay>
          {gridHelper.totalSelected > 0 && `${gridHelper.totalSelected} / `}
          {gridHelper.total || 0} {entityName}{gridHelper.total === 1 ? '' : 's'}
        </StyledQuantityDisplay>
        {!!filtersDefs.length && (
          <>
            <Divider />
            <Button
              ref={setReferenceElement}
              variant={!!appliedFilters.length ? 'contained' : 'borderless'}
              size="large"
              onClick={handleToggleFilter}
              disabled={isFetching}
            >
              <FilterIcon />
              <span>
                Filter
              </span>
            </Button>
          </>
        )}
        {onClickDelete && !!gridHelper.totalSelected && (
          <Button
            variant="borderless"
            color="error"
            size="large"
            onClick={onClickDelete}
          >
            <DeleteIcon />
            Delete ({gridHelper.totalSelected})
          </Button>
        )}
        {isColumnsChanged && (
          <>
            <Divider />
            <Button
              variant="borderless"
              color="primary"
              size="large"
              onClick={handleClickResetView}
            >
              Reset View
            </Button>
          </>
        )}
        <Divider />
        <Tooltip title="Advanced table features">
          <Button
            variant="icon"
            color="primary"
            size="large"
            onClick={() => setIsHelpVisible(true)}
          >
            <HelpIcon />
          </Button>
        </Tooltip>
        <Divider />
        <Tooltip title="Toggle compact mode">
          <Button
            variant="icon"
            color="primary"
            onClick={() => setCompactMode(!compactMode)}
          >
            {compactMode ? <ReorderLineIcon /> : <MenuIcon />}
          </Button>
        </Tooltip>
        {isLoadFailed && (
          <Button
            variant="borderless"
            color="error"
            size="large"
            onClick={() => {
              setIsLoadFailed(false);
              gridRef.current?.api.retryServerSideLoads();
            }}
          >
            Failed loading data...
            <RefreshIcon />
            Retry
          </Button>
        )}
        {onClickAdd && (
          <Button
            variant="borderless"
            size="large"
            pushRight
            onClick={onClickAdd}
          >
            <PlusIcon />
            <span>
              Add {entityName}
            </span>
          </Button>
        )}
      </ButtonsContainer>
      <Filters
        isOpen={isFiltersOpen}
        popperRef={setPopperElement}
        filters={filters}
        filtersDefs={filtersDefs}
        data={[]}
        setFilters={setFilters}
        clearAllFilters={clearAllFilters}
        onClose={() => setIsFiltersOpen(false)}
        style={styles.popper}
        attributes={attributes}
        subscriptionsOptions={subscriptionsOptions}
        contractLinesOptions={contractLinesOptions}
      />
      {!!appliedFilters.length && (
        <FilteredBy
          appliedFilters={appliedFilters}
          setFilter={setFilter}
          clearAllFilters={clearAllFilters}
        />
      )}
      <AgGridContainer
        $disabled={isDisabled}
        className="ag-theme-material"
      >
        <AgGridReact
          ref={gridRef}
          columnDefs={columnDefs}
          gridOptions={combinedGridOptions}
          noRowsOverlayComponent={customEmptyState || NoRowsOverlay}
          loadingOverlayComponent={LoadingOverlay}
          loadingCellRenderer={AgGridLoadingCellRenderer}
          rowHeight={compactMode ? 30 : 48}
          headerHeight={compactMode ? 40 : 48}
        />
        {isLoading && <LoadingBar isWithinTable />}
      </AgGridContainer>
      {isHelpVisible && (
        <FactaTableHelp
          onClose={() => setIsHelpVisible(false)}
          columnDefs={columnDefs}
          gridOptions={combinedGridOptions}
        />
      )}
    </>
  );
};
